'use client';

import * as React from 'react';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  SIDEBAR_WIDTH_MOBILE,
  type SidebarSide,
  type SidebarVariant,
} from '@/components/ui/sidebar-context';
import { cn } from '@/lib/utils';

function getSidebarGapClass(variant: SidebarVariant): string {
  return cn(
    'relative w-(--sidebar-width) bg-transparent transition-[width] duration-200 ease-linear',
    'group-data-[collapsible=offcanvas]:w-0',
    'group-data-[side=right]:rotate-180',
    variant === 'floating' || variant === 'inset'
      ? 'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]'
      : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon)',
  );
}

function getSidebarContainerClass(
  side: SidebarSide,
  variant: SidebarVariant,
  className: string | undefined,
): string {
  const sideClass =
    side === 'left'
      ? 'left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]'
      : 'right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]';
  const variantClass =
    variant === 'floating' || variant === 'inset'
      ? 'p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]'
      : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l';
  return cn(
    'fixed inset-y-0 z-10 hidden h-svh w-(--sidebar-width) transition-[left,right,width] duration-200 ease-linear md:flex',
    sideClass,
    variantClass,
    className,
  );
}

export function SidebarStatic({
  className,
  children,
  ...props
}: Readonly<React.ComponentProps<'div'>>) {
  return (
    <div
      data-slot="sidebar"
      className={cn(
        'flex h-full w-(--sidebar-width) flex-col bg-sidebar text-sidebar-foreground',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

interface SidebarMobileProps extends React.ComponentProps<'div'> {
  side: SidebarSide;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
}

export function SidebarMobile({
  side,
  openMobile,
  setOpenMobile,
  children,
  ...props
}: Readonly<SidebarMobileProps>) {
  return (
    <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
      <SheetContent
        data-sidebar="sidebar"
        data-slot="sidebar"
        data-mobile="true"
        className="w-(--sidebar-width) bg-sidebar p-0 text-sidebar-foreground [&>button]:hidden"
        style={
          {
            '--sidebar-width': SIDEBAR_WIDTH_MOBILE,
          } as React.CSSProperties
        }
        side={side}
      >
        <SheetHeader className="sr-only">
          <SheetTitle>Sidebar</SheetTitle>
          <SheetDescription>Displays the mobile sidebar.</SheetDescription>
        </SheetHeader>
        <div className="flex h-full w-full flex-col">{children}</div>
      </SheetContent>
    </Sheet>
  );
}

interface SidebarDesktopProps extends React.ComponentProps<'div'> {
  side: SidebarSide;
  variant: SidebarVariant;
  collapsible: 'offcanvas' | 'icon';
  state: 'expanded' | 'collapsed';
}

export function SidebarDesktop({
  side,
  variant,
  collapsible,
  state,
  className,
  children,
  ...props
}: Readonly<SidebarDesktopProps>) {
  return (
    <div
      className="group peer hidden text-sidebar-foreground md:block"
      data-state={state}
      data-collapsible={state === 'collapsed' ? collapsible : ''}
      data-variant={variant}
      data-side={side}
      data-slot="sidebar"
    >
      <div data-slot="sidebar-gap" className={getSidebarGapClass(variant)} />
      <div
        data-slot="sidebar-container"
        className={getSidebarContainerClass(side, variant, className)}
        {...props}
      >
        <div
          data-sidebar="sidebar"
          data-slot="sidebar-inner"
          className="flex h-full w-full flex-col bg-sidebar group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:border-sidebar-border group-data-[variant=floating]:shadow-sm"
        >
          {children}
        </div>
      </div>
    </div>
  );
}
